import { AnimatePresence } from 'framer-motion';
import { FC, useEffect, useMemo } from 'react';
import { SpaceKind } from 'shared';

import { PlusIcon } from '../../../../assets/icons/index';
import { PlusIconVariant } from '../../../../assets/icons/plus-icon';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import Button from '../../../../components/button/index';
import { LoadingCrossfade } from '../../../../components/loading-skeleton/index';
import { CrossfadePanel } from '../../../../components/motion/index';
import { VaultAccess } from '../../../../guards/page-access.util';
import { useScreenSize } from '../../../../hooks/index';
import { StringKey } from '../../../../lang/index';
import { DataRoomModalType } from '../../enums/data-room-modal-type.enum';
import { useAcceptedInviteRoom } from '../../hooks/use-accepted-invite-room.hook';
import { useDownloadSpaceArchive } from '../../hooks/use-download-space-archive.hook';
import { useRecipientNavigation } from '../../hooks/use-recipient-navigation.hook';
import { useRecipientRooms } from '../../hooks/use-recipient-rooms.hook';
import {
  DataRoomViewer,
  useDataRoomViewer,
  useVaultAccess,
} from '../../hooks/use-recipient-view.hook';
import { useRoomFromLink } from '../../hooks/use-room-from-link.hook';
import { useCreatableSpaceKinds } from '../../hooks/use-space-kinds.hook';
import { useSpacesHome } from '../../hooks/use-spaces-home.hook';
import { useSpacesNavigation } from '../../hooks/use-spaces-navigation.hook';
import { DataRoomModalStore } from '../../store/data-room-modal.store';
import { SidebarTreeStore } from '../../store/sidebar-tree.store';
import { HomeSpace, WorkspaceCardMenuAction } from '../../types/data-room.types';
import { isSelectedSpaceGone, withLatestSpace } from '../../utils/sidebar-tree.util';
import {
  spaceCardStatus,
  spaceDescription,
  toSelectedSpace,
} from '../../utils/space-list-item.util';
import { WorkspaceBrowserPage } from '../browser/workspace-browser-page';
import { CardActionHandler, SpaceCard } from '../components/space-card';
import { SpaceTile } from '../components/space-tile';
import { WorkspaceSectionLabel } from '../components/workspace-section-label';
import { WorkspacesHomePlaceholder } from '../components/workspaces-loading-placeholders';
import { RecipientHomeOverview } from './recipient-home-overview';

const HOME_PANEL_KEY = 'home';
const UNRESOLVED_PANEL_KEY = 'unresolved';

const SpaceSection: FC<{
  onAction: CardActionHandler;
  onOpen: (space: HomeSpace) => void;
  pendingArchiveIds: ReadonlySet<string>;
  spaces: HomeSpace[];
  title: StringKey;
}> = ({ onAction, onOpen, pendingArchiveIds, spaces, title }) => {
  if (!spaces.length) {
    return null;
  }

  return (
    <>
      <WorkspaceSectionLabel title={title} />
      <div className="flex flex-wrap gap-4">
        {spaces.map((space) => (
          <SpaceCard
            isDownloading={pendingArchiveIds.has(space.id)}
            key={space.id}
            onAction={onAction}
            onClick={() => onOpen(space)}
            space={space}
          />
        ))}
      </div>
    </>
  );
};

const SpaceTileSection: FC<{
  onOpen: (space: HomeSpace) => void;
  spaces: HomeSpace[];
  title: StringKey;
}> = ({ onOpen, spaces, title }) => {
  if (!spaces.length) {
    return null;
  }

  return (
    <>
      <WorkspaceSectionLabel title={title} />
      <div className="grid grid-cols-2 gap-3">
        {spaces.map((space) => (
          <SpaceTile
            description={spaceDescription(space)}
            key={space.id}
            kind={space.kind}
            name={space.name}
            onOpen={() => onOpen(space)}
            status={spaceCardStatus(toSelectedSpace(space))}
          />
        ))}
      </div>
    </>
  );
};

const HomeOverview: FC = () => {
  const { isMobile } = useScreenSize();
  const openModal = DataRoomModalStore(({ openModal }) => openModal);
  const reveal = SidebarTreeStore((state) => state.reveal);
  const { home, isPending } = useSpacesHome();
  const { downloadArchive, pendingArchiveIds } = useDownloadSpaceArchive();
  const creatableKinds = useCreatableSpaceKinds();

  const handleCardAction: CardActionHandler = (action, space) => {
    const metadata = { spaceId: space.id };
    const isWorkspace = space.kind === SpaceKind.WORKSPACE;

    switch (action) {
      case WorkspaceCardMenuAction.DELETE:
        openModal({
          metadata,
          type: isWorkspace
            ? DataRoomModalType.DELETE_WORKSPACE
            : DataRoomModalType.DELETE_DATA_ROOM,
        });
        break;
      // The whole space as one zip; the Vault card has no menu, so only a tree space gets here.
      case WorkspaceCardMenuAction.DOWNLOAD:
        void downloadArchive({
          space: { id: space.id, kind: isWorkspace ? SpaceKind.WORKSPACE : SpaceKind.DATA_ROOM },
        });
        break;
      case WorkspaceCardMenuAction.SETTINGS:
        openModal({
          metadata,
          type: isWorkspace
            ? DataRoomModalType.WORKSPACE_SETTINGS
            : DataRoomModalType.DATA_ROOM_SETTINGS,
        });
        break;
      // Only the data room menu offers it.
      case WorkspaceCardMenuAction.SHARE_ACCESS:
        openModal({ metadata, type: DataRoomModalType.MANAGE_ACCESS });
        break;
      case WorkspaceCardMenuAction.VIEW:
        reveal({ breadcrumbs: [], folderId: null, space: toSelectedSpace(space) });
        break;
    }
  };

  const workspaces: HomeSpace[] = home ? [home.vault, ...home.workspaces] : [];

  const openSpace = (space: HomeSpace) =>
    reveal({ breadcrumbs: [], folderId: null, space: toSelectedSpace(space) });

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col">
      {!isMobile && (
        <div className="flex items-center justify-between border-y border-gray-100 p-4">
          <h2 className="text-base font-semibold text-gray-700">
            <AppFormattedMessage id="HOME" />
          </h2>
          {/* The creatable kinds decide, a Guest Auditor's being none; the attribute keeps the
              auditor rule every write control here follows. One kind skips the type step. */}
          {creatableKinds.length > 0 && (
            <Button
              className="h-9 w-fit gap-1 py-1.5 pl-4 pr-5"
              data-permission-required
              onClick={() => openModal({ type: DataRoomModalType.CREATE_SPACE })}
              type="button"
            >
              <PlusIcon className="size-5" iconColor="#ffffff" variant={PlusIconVariant.VAULT} />
              <AppFormattedMessage id="CREATE" />
            </Button>
          )}
        </div>
      )}
      <LoadingCrossfade
        className="min-h-0 flex-1 overflow-y-auto"
        isLoading={isPending}
        skeleton={<WorkspacesHomePlaceholder />}
      >
        {() =>
          isMobile ? (
            <div className="flex flex-col gap-4">
              <SpaceTileSection
                onOpen={openSpace}
                spaces={workspaces}
                title="INTERNAL_WORKSPACES"
              />
              <SpaceTileSection
                onOpen={openSpace}
                spaces={home?.dataRooms ?? []}
                title="DATA_ROOMS"
              />
            </div>
          ) : (
            <div className="flex flex-col gap-4 px-4 py-4">
              <SpaceSection
                onAction={handleCardAction}
                onOpen={openSpace}
                pendingArchiveIds={pendingArchiveIds}
                spaces={workspaces}
                title="INTERNAL_WORKSPACES"
              />
              <SpaceSection
                onAction={handleCardAction}
                onOpen={openSpace}
                pendingArchiveIds={pendingArchiveIds}
                spaces={home?.dataRooms ?? []}
                title="DATA_ROOMS"
              />
            </div>
          )
        }
      </LoadingCrossfade>
    </div>
  );
};

export const WorkspacesHomePage: FC = () => {
  const storedSelection = SidebarTreeStore((state) => state.selection);
  const viewer = useDataRoomViewer();
  const isRecipientView = viewer === DataRoomViewer.RECIPIENT;
  // A Stakeholder is a recipient of the rooms shared with them and, beside them, reads their own
  // folders of the Vault: so they ask the module routes for that and for nothing else.
  const vaultAccess = useVaultAccess();
  const hasOwnVault = isRecipientView && vaultAccess === VaultAccess.OWN_FOLDERS;
  const { isPending: isNavigationPending, navigation } = useSpacesNavigation({
    enabled: viewer === DataRoomViewer.OWNER || hasOwnVault,
  });
  const { home, isPending: isHomePending } = useSpacesHome({ enabled: hasOwnVault });
  const { dataRooms, isPending: isRecipientPending } = useRecipientNavigation({
    enabled: isRecipientView,
  });
  // Home draws cards, the panel beside it draws a tree: two questions, two routes, as on the
  // owner's side.
  const { isPending: areRoomsPending, rooms } = useRecipientRooms({ enabled: isRecipientView });

  const roots = useMemo(() => {
    if (!isRecipientView) {
      return navigation
        ? [navigation.vault, ...navigation.workspaces, ...navigation.dataRooms]
        : [];
    }

    return hasOwnVault && navigation ? [navigation.vault, ...dataRooms] : dataRooms;
  }, [dataRooms, hasOwnVault, isRecipientView, navigation]);

  const areRootsLoaded = isRecipientView
    ? !isRecipientPending && (!hasOwnVault || !!navigation)
    : !!navigation;
  const resetSelection = SidebarTreeStore((state) => state.resetSelection);

  // Somebody who followed the room's own link out of an access email.
  useRoomFromLink({ areRootsLoaded, roots });

  // Somebody who followed an invitation link instead, and has just accepted it.
  useAcceptedInviteRoom({ areRootsLoaded, roots });

  // A space deleted elsewhere leaves its browser open on nothing; back to Home once the loaded
  // navigation no longer lists it.
  useEffect(() => {
    if (storedSelection && areRootsLoaded && isSelectedSpaceGone(storedSelection, roots)) {
      resetSelection();
    }
  }, [areRootsLoaded, resetSelection, roots, storedSelection]);

  const selection = useMemo(
    () => storedSelection && withLatestSpace(storedSelection, roots),
    [roots, storedSelection],
  );
  const isResolvingViewer = viewer === DataRoomViewer.UNRESOLVED;
  const panelKey = isResolvingViewer
    ? UNRESOLVED_PANEL_KEY
    : selection
      ? `${selection.space.kind}:${selection.space.id}`
      : HOME_PANEL_KEY;

  return (
    <AnimatePresence initial={false} mode="wait">
      <CrossfadePanel
        className="flex h-full min-h-0 min-w-0 flex-1 flex-col"
        key={panelKey}
        panelKey={panelKey}
        translate={false}
      >
        {/* Neither view until the session says which one. The owner's Home fetches on mount, so
            drawing it on a guess is a refused request, not just a flash of the wrong thing. */}
        {isResolvingViewer ? (
          <WorkspacesHomePlaceholder />
        ) : selection ? (
          <WorkspaceBrowserPage isRecipientView={isRecipientView} selection={selection} />
        ) : isRecipientView ? (
          <RecipientHomeOverview
            isPending={areRoomsPending || (hasOwnVault && (isHomePending || isNavigationPending))}
            rooms={rooms}
            vault={hasOwnVault ? home?.vault : undefined}
          />
        ) : (
          <HomeOverview />
        )}
      </CrossfadePanel>
    </AnimatePresence>
  );
};
